import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="invite-prospects"
export default class extends Controller {
  static targets = [
    "searchTerms",
    "searchTypeWrapper",
    "searchType",
    "utmCampaign",
    "utmSource",
    "pageTitle",
    "pageDescription",
    "spaceSelector",
    "invitationUrlHiddenInput",
    "nextButton",
    "publicHint",
    "publicInputs",
    "urlConfigurationWrapper",
    "urlConfigurationCheckbox"
  ]
  static values = {
    tapUrl: String,
    tapFeedId: String
  }

  initialize() {
    this.syncConfiguredPublicInvitationUrl = this.syncConfiguredPublicInvitationUrl.bind(this)
  }

  connect() {
    this.updateNextButtonDisabledState(true)

    document.addEventListener("tags-input:added", this.syncConfiguredPublicInvitationUrl)
    document.addEventListener("tags-input:deleted", this.syncConfiguredPublicInvitationUrl)

    this.handleSpaceSelectionChange()
  }

  disconnect() {
    document.removeEventListener("tags-input:added", this.syncConfiguredPublicInvitationUrl)
    document.removeEventListener("tags-input:deleted", this.syncConfiguredPublicInvitationUrl)
  }

  handleSpaceSelectionChange() {
    const selectedSpaceId = this.getSelectedSpaceId()

    if (!selectedSpaceId) {
      this.applyNoSpaceSelectedState()
      return
    }

    this.updateNextButtonDisabledState(false)

    if (this.isPublicSpaceId(selectedSpaceId)) {
      this.applyPublicSpaceSelectedState()
      return
    }

    this.applyPrivateSpaceSelectedState()
  }

  handleUrlConfigurationCheckboxChange() {
    if (!this.isPublicSpaceSelected()) {
      this.resetPublicUrlConfigurationState()
      return
    }

    this.applyPublicUrlConfigurationState()
  }

  syncConfiguredPublicInvitationUrl() {
    if (!this.isConfiguredPublicSpaceFlow()) {
      this.clearInvitationUrl()
      return
    }

    this.syncSearchTypeVisibility()
    this.invitationUrlHiddenInputTarget.value = this.buildConfiguredPublicInvitationUrl()
  }

  applyNoSpaceSelectedState() {
    this.hidePublicSettings()
    this.clearInvitationUrl()
    this.updateNextButtonDisabledState(true)
  }

  applyPublicSpaceSelectedState() {
    this.showPublicHint()
    this.showUrlConfigurationCheckbox()
    this.applyPublicUrlConfigurationState()
  }

  applyPrivateSpaceSelectedState() {
    this.hidePublicSettings()
    this.clearInvitationUrl()
  }

  applyPublicUrlConfigurationState() {
    if (!this.isConfiguredPublicSpaceFlow()) {
      this.resetPublicUrlConfigurationState()
      return
    }

    this.showPublicInputs()
    this.syncConfiguredPublicInvitationUrl()
  }

  resetPublicUrlConfigurationState() {
    this.hidePublicInputs()
    this.clearInvitationUrl()
  }

  buildConfiguredPublicInvitationUrl() {
    const queryParams = new URLSearchParams({
      "tap-dashboard": false,
      utm_medium: "link",
    })

    this.appendSearchTermsParams(queryParams)

    this.appendOptionalParams(queryParams)

    return `${this.tapUrlValue}?${queryParams.toString()}`
  }

  appendSearchTermsParams(queryParams) {
    const searchTerms = this.getSearchTerms()

    if (searchTerms.length) {
      searchTerms.forEach(term => queryParams.append("searchTerms", term))
    } else {
      this.searchTypeTarget.tomselect?.addItem("default")
    }
  }

  getSearchTerms() {
    return Array.from(this.searchTermsTarget?.children || [])
      .slice(0, -1)
      .map(child => child.textContent.trim())
  }

  appendOptionalParams(queryParams) {
    const optionalParams = {
      utm_source: this.utmSourceTarget?.value,
      utm_campaign: this.utmCampaignTarget?.value,
      pageTitle: this.encodeDisplayText(this.pageTitleTarget?.value),
      pageDescription: this.encodeDisplayText(this.pageDescriptionTarget?.value),
      searchType: this.searchTypeTarget?.value,
    }

    Object.entries(optionalParams).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value)
      }
    })
  }

  encodeDisplayText(value) {
    if (!value) {
      return value
    }

    const FOUR_PER_EM_SPACE = "\u2005"
    return value.replaceAll(" ", FOUR_PER_EM_SPACE)
  }

  syncSearchTypeVisibility() {
    const hasChildren = this.searchTermsTarget.children.length > 1
    this.searchTypeWrapperTarget.classList.toggle("hidden", !hasChildren)
  }

  getSelectedSpaceId() {
    const tomselect = this.getSpaceSelectorTomSelect()

    if (!tomselect) {
      return this.spaceSelectorTarget.value || null
    }

    return this.getSelectedSpaceIdFromTomSelect(tomselect)
  }

  getSelectedSpaceIdFromTomSelect(tomselect) {
    const selectedItemId = tomselect.items[0]

    if (selectedItemId) {
      return selectedItemId
    }

    const selectedValue = tomselect.getValue()

    if (Array.isArray(selectedValue)) {
      return selectedValue[0] || null
    }

    return selectedValue || this.spaceSelectorTarget.value || null
  }

  getSpaceSelectorTomSelect() {
    return this.spaceSelectorTarget.tomselect || null
  }

  isConfiguredPublicSpaceFlow() {
    return this.isPublicSpaceSelected() && this.urlConfigurationCheckboxTarget.checked
  }

  isPublicSpaceSelected() {
    return this.isPublicSpaceId(this.getSelectedSpaceId())
  }

  isPublicSpaceId(spaceId) {
    if (!spaceId) {
      return false
    }

    return String(spaceId) === String(this.tapFeedIdValue)
  }

  showPublicHint() {
    this.publicHintTarget.classList.remove("hidden")
  }

  showUrlConfigurationCheckbox() {
    this.urlConfigurationWrapperTarget.classList.remove("hidden")
  }

  showPublicInputs() {
    this.publicInputsTarget.classList.remove("hidden")
  }

  hidePublicInputs() {
    this.publicInputsTarget.classList.add("hidden")
    this.hideSearchType()
  }

  hidePublicSettings() {
    this.publicHintTarget.classList.add("hidden")
    this.urlConfigurationWrapperTarget.classList.add("hidden")
    this.hidePublicInputs()
  }

  hideSearchType() {
    this.searchTypeWrapperTarget.classList.add("hidden")
  }

  clearInvitationUrl() {
    this.invitationUrlHiddenInputTarget.value = ""
  }

  updateNextButtonDisabledState(disabled) {
    this.nextButtonTarget.disabled = disabled
  }
}
